// Firebase v9+ modular initialization.
//
// Loads and initializes the Firebase SDK from the npm-bundled dependency
// (`firebase` in package.json). Replaces the three v8 compat CDN <script>
// tags that previously lived in index.html — see the 2026-08-29 session
// log entry for the migration rationale.
//
// The primary driver isn't bundle size — it's offline-first. CDN-loaded
// Firebase means a cold launch with no connectivity would block on three
// network fetches to gstatic before auth could initialize. Bundling
// Firebase into the app is a hard prerequisite for the Capacitor Android
// wrap coming next: offline photo queue, offline tree browsing, and
// multi-day disconnected use all need auth+firestore ready on start.
//
// This module runs SYNCHRONOUSLY on import: config read → app init →
// auth + firestore + providers exported. Consumers (src/app.js and the
// auth-channels module) import `auth`, `db`, `googleProvider`,
// `facebookProvider`, and `useRedirect()` from here; SDK primitives
// (signInWithEmailAndPassword, collection, etc.) get imported directly
// from 'firebase/auth' / 'firebase/firestore' at the call site — that's
// the idiomatic v9 pattern and it lets Vite tree-shake precisely.

import { initializeApp, getApps } from 'firebase/app';
import {
    getAuth,
    getRedirectResult,
    GoogleAuthProvider,
    FacebookAuthProvider,
    browserLocalPersistence,
    indexedDBLocalPersistence,
    setPersistence,
} from 'firebase/auth';
import {
    initializeFirestore,
    persistentLocalCache,
    persistentMultipleTabManager,
} from 'firebase/firestore';

// window.EBIWAWA_CONFIG.FIREBASE is populated by /public/config.js which
// is loaded as a regular <script> BEFORE this ES module (module scripts
// default to defer). See index.html load order. If this is undefined,
// something's wrong with the deploy pipeline — fail loudly rather than
// swallowing the error like the pre-v9 try/catch did.
const firebaseConfig = window.EBIWAWA_CONFIG && window.EBIWAWA_CONFIG.FIREBASE;
if (!firebaseConfig) {
    throw new Error('firebase.js: window.EBIWAWA_CONFIG.FIREBASE is not set — check that public/config.js loaded before this module');
}

// Idempotent init — mirrors the old `if (!firebase.apps.length)` guard.
// In practice the ES module executes once per page load so this is
// belt-and-suspenders, but keep it for HMR safety during dev and for
// any future case where firebase.js is imported from a second entry.
export const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];

export const auth = getAuth(app);

// Session 2a: explicit IndexedDB persistence for auth. Web default is
// indexedDBLocalPersistence anyway (persists across tabs, survives
// cold launch), but being explicit means Capacitor Android's WebView
// gets the right behavior regardless of what its default might drift
// to. The setPersistence call is fire-and-forget (Firebase queues
// pending auth ops behind it); if it fails (e.g. private-window
// IndexedDB blocked), Firebase falls back to in-memory which loses
// the session on reload — acceptable degrade.
setPersistence(auth, indexedDBLocalPersistence)
    .catch(err => {
        console.warn('[firebase] IndexedDB auth persistence unavailable, falling back to memory:', err);
        return setPersistence(auth, browserLocalPersistence).catch(() => {});
    });

// Session 2a: Firestore with persistent local cache (IndexedDB).
// Previously `getFirestore(app)` — no offline cache, every read hit
// the network. `persistentLocalCache` enables IndexedDB-backed cache
// so cold launch in airplane mode can serve reads from the cache
// (Capacitor Android acceptance criterion). `persistentMultipleTabManager`
// coordinates across tabs / multiple WebView instances.
//
// Must be initializeFirestore (not getFirestore) — the latter can't
// take settings. This must run BEFORE any code calls getFirestore
// elsewhere in the app; since firebase.js is the single init point,
// that ordering is enforced by import.
export const db = initializeFirestore(app, {
    localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
    }),
});

// Provider singletons — constructed once, reused across every social
// sign-in call. Previously constructed inline as
// `new firebase.auth.GoogleAuthProvider()` at each call site; that
// pattern still worked but this cleaner setup lets the platform-branched
// popup vs redirect wrapper (below) reuse the same instances.
export const googleProvider = new GoogleAuthProvider();
export const facebookProvider = new FacebookAuthProvider();

// Platform detection for popup vs redirect on social auth.
//
// `signInWithPopup` is unreliable inside an Android WebView (popups
// often can't complete the auth cycle back to the host page). Capacitor
// exposes itself on window when the app runs in a native shell, so we
// key on that.
//
// Absent-Capacitor path returns false, which keeps desktop web on the
// popup flow (better UX, no full-page redirect). This function is safe
// to call from any environment — the optional chaining short-circuits
// cleanly if Capacitor isn't loaded.
export function useRedirect() {
    return !!(
        window.Capacitor &&
        typeof window.Capacitor.isNativePlatform === 'function' &&
        window.Capacitor.isNativePlatform()
    );
}

// Session 2b Phase 1 (Task 3a): getRedirectResult completion.
//
// signInWithRedirect() navigates the page (or reloads the WebView) to the
// provider's auth flow. When the flow completes, control returns to the app —
// but the credential isn't automatically consumed. getRedirectResult(auth)
// reads it once, then future calls return null. Must be called at app boot
// on every load; onAuthStateChanged will fire naturally afterwards with the
// authenticated user.
//
// The promise resolves to:
//   - a UserCredential if a redirect flow just completed
//   - null if there's no pending redirect (normal app boot)
//   - rejects with an auth/* error on failure (wrong SHA-1, blocked domain,
//     network fail, popup-closed-by-user, etc.)
//
// Called from app.js immediately after firebase.js loads. Errors are logged
// but non-fatal — auth state listener handles the case where the user is
// still signed out (i.e. redirect result was null OR errored).
export async function consumeRedirectResult() {
    try {
        const result = await getRedirectResult(auth);
        if (result) {
            // onAuthStateChanged will fire with result.user; app.js's own
            // handler (including _handleIdpPostSignin for invite consumption)
            // picks it up from there. We deliberately don't do post-signin
            // processing here to keep firebase.js free of app-level concerns.
            return result;
        }
        return null;
    } catch (err) {
        console.warn('[firebase] getRedirectResult error:', err && err.code, err && err.message);
        return null;
    }
}
